import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePersistentState } from "@traas/lib/src/util/usePersistentState";
import { useApi } from "../api/APIContext";
import { Block, CanvasContext, PlannedProject, StagedChangesContext } from "./services/types";
import { Task } from "../../../lib/src/api/dto/Task";
import { useInterval } from "@traas/lib/src/util/useInterval";
import { useBreakpoints } from "@traas/lib/src/util/useBreakpoints";
import moment from "moment";
import { useSelectedDay } from "./DateContext";
import { Project } from "../../../lib/src/api/dto/Project";
import { durationMinutesToSlots, timeToSlot } from "./services/slots";
import {
  getDefaultTaskDuration,
  suggestPlanningForTask,
  willTaskCollideWithOtherBlocks,
} from "./services/planningStrategy";
import { getBlocksInCanvas, getCanvasId, parseBufferDate, parseCanvasId } from "./services/canvas";
import { buildScaleConfig, PlannerScaleConfig } from "./services/scale";
import { useRealtimeConnection } from "@traas/lib/src/realtime/RealtimeConnectionContext";

export const ALL_TEAMS_CATEGORY = "Alle teams";

export interface MousePosition {
  x: number;
  y: number;
}
export interface PlannerContextType {
  // Project/task data
  projectMap: Map<string, Project>;
  taskMap: Map<string, Task>;
  projectTasksMap: Map<string, Task[]>;
  getPlannedTasksForDay: (date: Date) => Task[];
  getPlannedTasksForWeek: (date: Date) => Task[];
  isTaskSaving: (taskId: string) => boolean;
  suggestPlanningForTask: (
    task: Task,
    teamId: string,
    date: Date,
  ) => { duration: number; durationInMinutes: number; startAt: Date; slot: number } | null;

  // Floating stuff
  day: Date;
  setDay: React.Dispatch<React.SetStateAction<Date>>;
  onMouseDown: (project: string, mode: "move" | "resize_start" | "float" | "resize_end") => void;
  plannedProjects: PlannedProject[];
  area: "world" | "drawer" | string;
  setArea: (area: "world" | "drawer" | string) => void;
  slot: number;
  setSlot: (slot: number) => void;
  mode: "idle" | "float" | "dropped" | "move" | "resize_start" | "resize_end";
  floatingTask: Task | null;
  mousePos: MousePosition | null;

  // Notifications
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, "id" | "createdAt">) => void;
  dismissNotification: (notification: Notification) => void;

  // Category
  category: string;
  setCategory: React.Dispatch<React.SetStateAction<string>>;

  // Canvas
  getBlocksInCanvas: (date: Date, teamId: string) => Block<Task>[];
  bufferSize: "w-72" | "w-80" | "w-96";
  setBufferSize: (size: "w-72" | "w-80" | "w-96") => void;

  // Focus
  focussedTask: string | null;
  focusTask: (task: Task) => void;
  clearFocussedTask: () => void;

  // Scale
  scaleConfig: PlannerScaleConfig;
  setScale: (scale: number) => void;
}
export interface PlannerContextProps {
  children: React.ReactNode;
}

const PlannerContext = React.createContext<PlannerContextType>({} as PlannerContextType);

export interface Notification {
  id: string;
  title: string;
  description: string;
  level: "info" | "warning" | "error";
  createdAt: Date;
}
export function PlannerContextProvider(props: PlannerContextProps) {
  const {
    projects,
    tasks,
    planTask,
    unPlanTask,
    taskToBuffer,
    resetLocalChanges,
    contextDate,
    setContextDate,
    quantityTypes,
  } = useApi();
  const { day, setDay } = useSelectedDay();
  const [currentPos, setCurrentPos] = useState<MousePosition | null>(null);
  const [mode, setMode] = useState<"idle" | "float" | "move" | "dropped" | "resize_start" | "resize_end">("idle");
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [actionStartPos, setActionStartPos] = useState<MousePosition | null>(null);
  const [area, setArea] = useState<"world" | "drawer" | string>("world");
  const [slot, setSlot] = useState<number>(0);

  const [scale, setScale] = useState(1);

  const [focussedTask, setFocussedTask] = useState<string | null>(null);

  const { isMobile } = useBreakpoints();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const addNotification = useCallback((notification: Omit<Notification, "id" | "createdAt">) => {
    setNotifications((old) => [
      ...old,
      {
        ...notification,
        id: Math.random().toString(36).slice(2, 9),
        createdAt: new Date(),
      },
    ]);
  }, []);
  const dismissNotification = useCallback((notification: Notification) => {
    setNotifications((old) => old.filter((n) => n.id !== notification.id));
  }, []);

  const lockedTaskIds = useMemo(() => {
    return tasks.filter((t) => t.isLocked).map((t) => t.id);
  }, [tasks]);

  const [lastSentDate, setLastSentDate] = useState<string>();
  const ctx = useRealtimeConnection();
  const dateSelected = (date: Date, skipDebounce = false) => {
    if (!skipDebounce && lastSentDate === date.toISOString()) {
      return;
    }

    if (ctx.status !== "online") {
      return;
    }
    setLastSentDate(date.toISOString());
    ctx.send({ type: "date_selected", data: { date: date.toISOString() } });
  };

  useEffect(() => {
    dateSelected(day);
  }, [day]);
  useInterval(() => {
    dateSelected(day, true);
  }, 3000);

  useEffect(() => {
    if (contextDate && day && Math.abs(moment(day).diff(contextDate, "days")) > 90) {
      setContextDate(day);
    }
  }, [day, contextDate]);

  const scaleConfig = useMemo(() => buildScaleConfig(scale), [scale]);

  /**
   * Planning
   */
  const getPlannedTasksForDay = useCallback(
    (date: Date): Task[] => {
      return tasks.filter((t) => {
        return (
          t.startAt &&
          t.durationMinutes &&
          t.teamId &&
          t.startAt.getFullYear() === date.getFullYear() &&
          t.startAt.getMonth() === date.getMonth() &&
          t.startAt.getDate() === date.getDate()
        );
      }, []);
    },
    [tasks],
  );
  const getPlannedTasksForWeek = useCallback(
    (date: Date): Task[] => {
      const startOfWeek = moment(date).startOf("week");
      const endOfWeek = moment(date).endOf("week");
      return tasks.filter((t) => {
        if (!(t.startAt && t.durationMinutes && t.teamId)) {
          // Task is not properly planned
          return false;
        }
        const startAt = moment(t.startAt!);
        return startAt.isAfter(startOfWeek) && startAt.isBefore(endOfWeek);
      }, []);
    },
    [tasks],
  );
  const projectMap = useMemo(() => {
    return projects.reduce((map, project) => {
      map.set(project.id, project);
      return map;
    }, new Map<string, Project>());
  }, [projects]);
  const taskMap = useMemo(() => {
    return tasks.reduce((map, task) => {
      map.set(task.id, task);
      return map;
    }, new Map<string, Task>());
  }, [tasks]);
  const projectTasksMap = useMemo(() => {
    return tasks.reduce((map, task) => {
      if (!task.projectId) {
        return map;
      }
      if (!map.has(task.projectId)) {
        map.set(task.projectId, []);
      }
      map.get(task.projectId)?.push(task);
      return map;
    }, new Map<string, Task[]>());
  }, [tasks]);

  const [stagedBlockChanges, setStagedBlockChanges] = useState<StagedChangesContext>(new Map());
  const blocks = useMemo(() => {
    return tasks
      .filter((t) => {
        if (stagedBlockChanges.get(t.id)?.unPlan) {
          return false;
        }
        const isFullyInitialized = t.startAt && t.teamId && t.durationMinutes;
        const isBeingCreated = stagedBlockChanges.has(t.id);
        return isFullyInitialized || isBeingCreated;
      })
      .map((task) => {
        if (!task.startAt && stagedBlockChanges.has(task.id)) {
          // In this case, the task is being created
          return stagedBlockChanges.get(task.id)!;
        }
        const originalBlock = {
          id: task.id,
          slot: timeToSlot(task.startAt!),
          duration: durationMinutesToSlots(task.durationMinutes!),
          canvasId: getCanvasId(task.startAt!, task.teamId!),
          details: task,
        };
        if (!stagedBlockChanges.has(task.id)) {
          return originalBlock;
        }
        const stagedBlock = stagedBlockChanges.get(task.id)!;
        return {
          ...originalBlock,
          ...stagedBlock,
        };
      })
      .filter((block) => {
        return block.details?.startAt && block.details?.teamId && block.details?.durationMinutes && block.canvasId;
      }) as Block<Task>[];
  }, [tasks, stagedBlockChanges]);

  const blocksByCanvas: CanvasContext = useMemo(() => {
    return blocks.reduce((map, block) => {
      map.set(block.canvasId, [...(map.get(block.canvasId) ?? []), block]);
      return map;
    }, new Map<string, Block<Task>[]>());
  }, [blocks]);
  const blockById = useMemo(() => {
    return blocks.reduce((map, block) => {
      map.set(block.id, block);
      return map;
    }, new Map<string, Block<Task>>());
  }, [blocks]);

  const updateBlock = (id: string, fields: Partial<Block<Task>> | null) => {
    setStagedBlockChanges((oldMap) => {
      const newMap = new Map(oldMap.entries());
      if (fields === null) {
        newMap.set(id, { unPlan: true });
        return newMap;
      }
      if (!newMap.has(id)) {
        newMap.set(id, fields);
        return newMap;
      }
      const oldFields = newMap.get(id)!;
      newMap.set(id, { ...oldFields, unPlan: undefined, ...fields });
      return newMap;
    });
  };
  const inTeamArea = useMemo(() => area.includes(":"), [area]);
  const inBufferArea = useMemo(() => area.includes("buffer-"), [area]);
  const floatingTask = useMemo(() => {
    const selected = selectedTask ? taskMap.get(selectedTask) : undefined;
    const isPlanned = selectedTask && blockById.has(selectedTask);
    if ((!isPlanned && area !== "drawer" && mode === "float") || (area === "drawer" && mode === "move")) {
      return selected ?? null;
    }
    return null;
  }, [taskMap, selectedTask, blockById, area, mode]);

  const [saving, setSaving] = useState(false);
  const isTaskSaving = useCallback(
    (taskId: string): boolean => {
      return stagedBlockChanges.has(taskId) && saving;
    },
    [stagedBlockChanges, saving],
  );
  useEffect(() => {
    if (mode === "idle" && stagedBlockChanges.size > 0 && !saving) {
      setSaving(true);
      // There are unsaved changes, save them
      let promises: Promise<string | null>[] = [];
      for (const taskId of stagedBlockChanges.keys()) {
        const block = blockById.get(taskId);
        if (!block) {
          const task = taskMap.get(taskId)!;
          if (!task) continue;
          // We should check if the block has a date, even though it has no team. This case is for tasks that are in the buffer.
          const stagedBlockChange = stagedBlockChanges.get(taskId)!;
          if (stagedBlockChange.details?.startAt && !stagedBlockChange.details?.teamId) {
            // This is a task that should be in the buffer
            promises.push(
              taskToBuffer(task, stagedBlockChange.details.startAt).then(() => {
                return taskId;
              }),
            );
          } else {
            promises.push(
              unPlanTask(task).then(() => {
                return taskId;
              }),
            );
          }
        } else {
          const { teamId, date } = parseCanvasId(block.canvasId);
          date.setHours(Math.floor(block.slot / 4 + 6), (block.slot * 15) % 60, 0, 0);
          promises.push(
            planTask(block.details, teamId, date, block.duration * 15)
              .then(() => {
                return taskId;
              })
              .catch((e) => {
                let description = e.message;
                addNotification({
                  title: "Fout bij opslaan",
                  description: description,
                  level: "error",
                });
                setStagedBlockChanges((oldMap) => {
                  return new Map([...oldMap.entries()].filter(([id]) => id !== taskId));
                });
                throw e;
              }),
          );
        }
      }
      // Update the staged changed by deleting only the ones that were successfully updated
      Promise.all(promises)
        .then(async (updatedIds) => {
          await new Promise((resolve) => setTimeout(resolve, 100));
          setStagedBlockChanges((oldMap) => {
            return new Map([...oldMap.entries()].filter(([id]) => !updatedIds.includes(id)));
          });
          await new Promise((resolve) => setTimeout(resolve, 100));
        })
        .catch(async (e) => {
          await resetLocalChanges();
          throw e;
        })
        .finally(() => setSaving(false));
    }
  }, [stagedBlockChanges, blockById, planTask, taskMap, mode, saving, resetLocalChanges]);
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      setCurrentPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("mousemove", onMouseMove);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const onMouseUp = () => {
    setMode((oldMode) => {
      if (oldMode === "float") {
        return "dropped";
      }
      return "idle";
    });
    setActionStartPos(null);
  };
  const onMouseDown = useCallback(
    (task: string, mode: "move" | "float" | "resize_start" | "resize_end") => {
      if (isMobile) {
        console.info("Ignoring mouse down event on mobile");
        return;
      }
      if (lockedTaskIds.includes(task)) {
        console.info("Ignoring mouse down event on locked task");
        return;
      }
      setSelectedTask(task);
      setMode(mode);
      setActionStartPos(currentPos);
    },
    [currentPos, isMobile, lockedTaskIds],
  );

  const dropTaskIfPossible = useCallback(
    (taskId: string, areaType: "team" | "buffer" | "world") => {
      const task = taskMap.get(taskId);
      if (!task) {
        return;
      }
      console.info("Attempting to drop task", task);
      if (areaType === "team") {
        const { teamId, date } = parseCanvasId(area);
        const suggestion = suggestPlanningForTask(blocksByCanvas, task, quantityTypes, teamId, date, slot);
        if (suggestion) {
          updateBlock(task.id, {
            details: {
              ...task,
              startAt: date,
              durationMinutes: suggestion.durationInMinutes,
              teamId: teamId,
            },
            id: task.id,
            slot: suggestion.slot,
            duration: suggestion.duration,
            canvasId: area,
          });
          return;
        }
      } else if (areaType === "world") {
        const update = {
          details: {
            ...task,
            startAt: null,
            durationMinutes: null,
            teamId: null,
          },
          id: task.id,
          slot: 4,
          duration: getDefaultTaskDuration(task, quantityTypes),
          canvasId: area,
        };
        updateBlock(task.id, update);
      } else {
        const date = parseBufferDate(area);
        const update = {
          details: {
            ...task,
            startAt: date,
            durationMinutes: null,
            teamId: null,
          },
          id: task.id,
          slot: 4,
          duration: getDefaultTaskDuration(task, quantityTypes),
          canvasId: area,
        };
        updateBlock(task.id, update);
      }
    },
    [area, slot, taskMap, updateBlock, quantityTypes, blocksByCanvas],
  );

  useEffect(() => {
    if (mode === "dropped") {
      if (selectedTask && inBufferArea) {
        dropTaskIfPossible(selectedTask, "buffer");
      } else if (selectedTask && inTeamArea) {
        dropTaskIfPossible(selectedTask, "team");
      } else if (selectedTask && area === "world") {
        dropTaskIfPossible(selectedTask, "world");
      }
      setMode("idle");
      setSelectedTask(null);
    }
  }, [mode, selectedTask, dropTaskIfPossible, inTeamArea, inBufferArea, area]);

  useEffect(() => {
    if (mode === "idle") {
      return;
    }
    if (mode === "move") {
      const block = selectedTask ? blockById.get(selectedTask) : undefined;
      if (!block) {
        return;
      }
      if (area !== block.canvasId) {
        // This should be sufficient?
        if (selectedTask) {
          console.debug({ area, block, selectedTask });
          updateBlock(selectedTask, null);
          setMode("float");
        }
      }
      if (selectedTask && currentPos && actionStartPos) {
        if (Math.abs(currentPos.x - actionStartPos.x) > scaleConfig.slotSize) {
          if (block) {
            const delta = Math.round((currentPos.x - actionStartPos.x) / scaleConfig.slotSize);
            const newTimeslot = Math.min(44 - block.duration, Math.max(0, block.slot + delta));
            const { teamId, date } = parseCanvasId(block.canvasId);
            if (
              willTaskCollideWithOtherBlocks(blocksByCanvas, block.details, teamId, date, newTimeslot, block.duration)
            ) {
              return;
            }
            updateBlock(selectedTask, { slot: newTimeslot });
            setActionStartPos((start) => (start ? { ...start, x: start.x + delta * scaleConfig.slotSize } : null));
          }
        }
      }
    }
    if (mode === "resize_start" && selectedTask && currentPos && actionStartPos) {
      if (Math.abs(currentPos.x - actionStartPos.x) > scaleConfig.slotSize) {
        const block = selectedTask ? blockById.get(selectedTask) : undefined;
        if (block) {
          let delta = Math.round((currentPos.x - actionStartPos.x) / scaleConfig.slotSize);
          delta = Math.max(-block.slot, delta);
          delta = Math.min(block.duration - 2, delta);
          if (delta === 0) {
            return;
          }
          const newDuration = block.duration - delta;
          const newTimeslot = block.slot + delta;
          const { teamId, date } = parseCanvasId(block.canvasId);
          if (willTaskCollideWithOtherBlocks(blocksByCanvas, block.details, teamId, date, newTimeslot, newDuration)) {
            return;
          }
          updateBlock(selectedTask, { slot: newTimeslot, duration: newDuration });
          setActionStartPos((start) => (start ? { ...start, x: start.x + delta * scaleConfig.slotSize } : null));
        }
      }
    }
    if (mode === "resize_end" && selectedTask && currentPos && actionStartPos) {
      if (Math.abs(currentPos.x - actionStartPos.x) > scaleConfig.slotSize) {
        const block = selectedTask ? blockById.get(selectedTask) : undefined;
        if (block) {
          let delta = Math.round((currentPos.x - actionStartPos.x) / scaleConfig.slotSize);
          delta = Math.max(-block.duration + 2, delta);
          delta = Math.min(44 - block.duration - block.slot, delta);
          if (delta === 0) {
            return;
          }
          const newDuration = block.duration + delta;
          const { teamId, date } = parseCanvasId(block.canvasId);
          if (willTaskCollideWithOtherBlocks(blocksByCanvas, block.details, teamId, date, block.slot, newDuration)) {
            return;
          }
          updateBlock(selectedTask, { duration: newDuration });
          setActionStartPos((start) => (start ? { ...start, x: start.x + delta * scaleConfig.slotSize } : null));
        }
      }
    }
  }, [currentPos, mode, selectedTask, blockById, actionStartPos, scaleConfig, blocksByCanvas]);
  const [category, setCategory] = usePersistentState<string>("team_category", ALL_TEAMS_CATEGORY);
  const [bufferSize, setBufferSize] = usePersistentState<"w-72" | "w-80" | "w-96">("buffer_size", "w-72");
  const context: PlannerContextType = {
    getPlannedTasksForDay,
    getPlannedTasksForWeek,
    isTaskSaving,
    projectMap,
    taskMap,
    projectTasksMap,
    day,
    setDay,
    suggestPlanningForTask: (task: Task, teamId: string, date: Date) =>
      suggestPlanningForTask(blocksByCanvas, task, quantityTypes, teamId, date, slot),
    onMouseDown,
    plannedProjects: [],
    setArea,
    slot,
    setSlot,
    mode,
    area,
    floatingTask,
    mousePos: currentPos,
    getBlocksInCanvas: (date: Date, teamId: string) => getBlocksInCanvas(blocksByCanvas, date, teamId),
    scaleConfig,
    setScale,
    notifications,
    addNotification,
    dismissNotification,
    category,
    setCategory,
    bufferSize,
    setBufferSize,
    focussedTask,
    focusTask: (task: Task) => {
      if (task.startAt) {
        setDay(task.startAt);
      }
      setFocussedTask(task.id);
    },
    clearFocussedTask: () => setFocussedTask(null),
  };
  return <PlannerContext.Provider value={context}>{props.children}</PlannerContext.Provider>;
}
export function usePlanner(): PlannerContextType {
  return React.useContext(PlannerContext);
}
